/**
 * Validation utilities for form inputs
 * Provides industry-standard validation patterns and error messages
 */

const VALIDATION_PATTERNS = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  // At least 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char
  strongPassword: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
  // At least 1 uppercase, 1 lowercase, 1 number, 8+ chars
  moderatePassword: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/,
  name: /^[a-zA-Z\s'-]{2,}$/,
};

const VALIDATION_RULES = {
  name: {
    required: true,
    minLength: 2,
    maxLength: 100,
    pattern: VALIDATION_PATTERNS.name,
    errorMessages: {
      required: "Name is required",
      minLength: "Name must be at least 2 characters",
      maxLength: "Name cannot exceed 100 characters",
      pattern: "Name can only contain letters, spaces, hyphens, and apostrophes",
    },
  },
  email: {
    required: true,
    maxLength: 255,
    pattern: VALIDATION_PATTERNS.email,
    errorMessages: {
      required: "Email is required",
      maxLength: "Email cannot exceed 255 characters",
      pattern: "Please enter a valid email address",
    },
  },
  password: {
    required: true,
    minLength: 8,
    maxLength: 128,
    errorMessages: {
      required: "Password is required",
      minLength: "Password must be at least 8 characters",
      maxLength: "Password cannot exceed 128 characters",
      weak: "Password must contain uppercase, lowercase, numbers, and special characters (@$!%*?&)",
    },
  },
  role: {
    required: true,
    allowedValues: ["admin", "member"],
    errorMessages: {
      required: "Role is required",
      invalid: "Invalid role selected",
    },
  },
};

/**
 * Validate a single field based on predefined rules
 * @param {string} fieldName - Name of the field to validate
 * @param {string} value - Value of the field
 * @param {object} customRules - Optional custom validation rules
 * @returns {string|null} - Error message or null if valid
 */
export const validateField = (fieldName, value, customRules = {}) => {
  const rules = customRules[fieldName] || VALIDATION_RULES[fieldName];

  if (!rules) {
    return null; // No rules defined for this field
  }

  const trimmedValue = typeof value === "string" ? value.trim() : value;

  // Check required
  if (rules.required && !trimmedValue) {
    return rules.errorMessages.required || "This field is required";
  }

  // Skip further validation if empty and not required
  if (!trimmedValue && !rules.required) {
    return null;
  }

  // Check minLength
  if (rules.minLength && trimmedValue.length < rules.minLength) {
    return rules.errorMessages.minLength;
  }

  // Check maxLength
  if (rules.maxLength && trimmedValue.length > rules.maxLength) {
    return rules.errorMessages.maxLength;
  }

  // Check pattern
  if (rules.pattern && !rules.pattern.test(trimmedValue)) {
    return rules.errorMessages.pattern;
  }

  // Check allowed values
  if (rules.allowedValues && !rules.allowedValues.includes(trimmedValue)) {
    return rules.errorMessages.invalid;
  }

  return null;
};

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @returns {object} - {isValid: boolean, strength: 'weak'|'moderate'|'strong', message: string}
 */
export const validatePasswordStrength = (password) => {
  const minLengthError = validateField("password", password);
  if (minLengthError) {
    return {
      isValid: false,
      strength: "weak",
      message: minLengthError,
    };
  }

  if (VALIDATION_PATTERNS.strongPassword.test(password)) {
    return {
      isValid: true,
      strength: "strong",
      message: "Strong password",
    };
  }

  if (VALIDATION_PATTERNS.moderatePassword.test(password)) {
    return {
      isValid: true,
      strength: "moderate",
      message: "Password is acceptable but could be stronger. Add special characters (@$!%*?&) for extra security",
    };
  }

  return {
    isValid: false,
    strength: "weak",
    message: VALIDATION_RULES.password.errorMessages.weak,
  };
};

/**
 * Validate entire user form
 * @param {object} formData - Form data object
 * @param {object} options - Validation options
 * @returns {object} - {isValid: boolean, errors: object}
 */
export const validateUserForm = (formData, options = {}) => {
  const { validatePassword = true } = options;
  const errors = {};

  // Validate name
  const nameError = validateField("name", formData.name);
  if (nameError) errors.name = nameError;

  // Validate email
  const emailError = validateField("email", formData.email);
  if (emailError) errors.email = emailError;

  // Validate role
  const roleError = validateField("role", formData.role);
  if (roleError) errors.role = roleError;

  // Validate password if required
  if (validatePassword && formData.password) {
    const passwordStrength = validatePasswordStrength(formData.password);
    if (!passwordStrength.isValid) {
      errors.password = passwordStrength.message;
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Validate password reset form
 * @param {object} formData - Form data with newPassword and confirmPassword
 * @returns {object} - {isValid: boolean, errors: object}
 */
export const validatePasswordResetForm = (formData) => {
  const errors = {};

  // Validate new password strength
  const passwordStrength = validatePasswordStrength(formData.newPassword);
  if (!passwordStrength.isValid) {
    errors.newPassword = passwordStrength.message;
  }

  // Validate confirm password
  if (!formData.confirmPassword) {
    errors.confirmPassword = "Please confirm your password";
  } else if (formData.newPassword !== formData.confirmPassword) {
    errors.confirmPassword = "Passwords do not match";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Parse and categorize API errors
 * @param {object} error - Axios error object
 * @returns {object} - {message: string, code: string, details: object}
 */
export const parseAPIError = (error) => {
  // Handle network errors
  if (!error.response) {
    return {
      message: "Network error. Please check your connection and try again.",
      code: "NETWORK_ERROR",
      details: error.message,
    };
  }

  const { status, data } = error.response;

  // Handle validation errors (422)
  if (status === 422 && data.errors) {
    return {
      message: "Validation failed",
      code: "VALIDATION_ERROR",
      details: data.errors,
    };
  }

  // Handle authentication errors (401)
  if (status === 401) {
    return {
      message: "Your session has expired. Please log in again.",
      code: "AUTH_ERROR",
      details: data.message,
    };
  }

  // Handle authorization errors (403)
  if (status === 403) {
    return {
      message: "You don't have permission to perform this action.",
      code: "FORBIDDEN_ERROR",
      details: data.message,
    };
  }

  // Handle not found errors (404)
  if (status === 404) {
    return {
      message: "The requested resource was not found.",
      code: "NOT_FOUND_ERROR",
      details: data.message,
    };
  }

  // Handle conflict errors (409) - e.g., duplicate email
  if (status === 409) {
    return {
      message: data.message || "This resource already exists. Please check and try again.",
      code: "CONFLICT_ERROR",
      details: data.message,
    };
  }

  // Handle server errors (5xx)
  if (status >= 500) {
    return {
      message: "Server error. Please try again later.",
      code: "SERVER_ERROR",
      details: data.message,
    };
  }

  // Handle other errors
  return {
    message: data.message || "An error occurred. Please try again.",
    code: "UNKNOWN_ERROR",
    details: data,
  };
};

/**
 * Get user-friendly error message for display
 * @param {string|object} error - Error or error message
 * @returns {string} - User-friendly error message
 */
export const getUserFriendlyErrorMessage = (error) => {
  if (typeof error === "string") {
    return error;
  }

  if (error.message) {
    return error.message;
  }

  return "An unexpected error occurred. Please try again.";
};

export default {
  validateField,
  validatePasswordStrength,
  validateUserForm,
  validatePasswordResetForm,
  parseAPIError,
  getUserFriendlyErrorMessage,
};
